/**
 * The notification event taxonomy, mirrored from `CommunicationEventTypes.cs` (`Defaults` line
 * 261 / `EmailDefaults` line 370 / `ChatDefaults` line 467) and the owner's kms delta to
 * `Settings/Notifications.tsx` (+364) — see W9 §A.5.1 and §D.2.
 *
 * This table is the single source both the settings section and `fakeNotifications.ts` read, so
 * an invented event id (the pre-W9 section had four: `document.mention`, `document.shared`,
 * `collection.created`, `digest.weekly` — none of which exist server-side) fails a test instead
 * of shipping. It also carries the estate's one structural guarantee: **no event defaults email
 * on** — the exact thing the pre-W9 section got backwards for `document.mention`.
 */

export type NotificationChannel = "app" | "email" | "chat";

export type NotificationSection = "documents" | "workspace" | "automation" | "pulse" | "fromKnowIt";

export interface NotificationEventDef {
  id: string;
  section: NotificationSection;
  label: string;
  description: string;
  /** In-app default. Only six events default off; every other event defaults on. */
  appDefault: boolean;
  /** Always false — email is opt-in, estate-wide, with no exception in this table. */
  emailDefault: false;
  /** Not user-settable through any endpoint slate has (§A.5.1) — rendered nowhere, but kept
   * here so the table stays a faithful mirror of the server defaults. */
  chatDefault: boolean;
  /** Members-only row; hidden from a non-admin viewer. */
  adminOnly?: true;
  /** Cloud deployments only — hidden on a self-hosted workspace. */
  cloudOnly?: true;
}

export const NOTIFICATION_EVENTS: NotificationEventDef[] = [
  // ── Documents and comments ──────────────────────────────────────────────────────────────────
  {
    id: "documents.publish",
    section: "documents",
    label: "A document is published",
    description: "Someone publishes a document you can see.",
    appDefault: false,
    emailDefault: false,
    chatDefault: false,
  },
  {
    id: "documents.update",
    section: "documents",
    label: "A document you follow changes",
    description: "A document you created or subscribed to is updated.",
    appDefault: true,
    emailDefault: false,
    chatDefault: false,
  },
  {
    id: "comments.create",
    section: "documents",
    label: "A new comment",
    description: "Someone comments on a document you created or subscribed to.",
    appDefault: true,
    emailDefault: false,
    chatDefault: false,
  },
  {
    id: "comments.mentioned",
    section: "documents",
    label: "You're mentioned in a comment",
    description: "Someone @mentions you inside a comment thread.",
    appDefault: true,
    emailDefault: false,
    chatDefault: false,
  },
  {
    id: "comments.resolve",
    section: "documents",
    label: "A comment thread is resolved",
    description: "A comment thread you took part in is marked resolved.",
    appDefault: true,
    emailDefault: false,
    chatDefault: false,
  },
  {
    id: "document_chat_mention",
    section: "documents",
    label: "You're mentioned in a document chat",
    description: "Someone @mentions you inside a document's own chat, not a margin comment.",
    appDefault: true,
    emailDefault: false,
    chatDefault: false,
  },
  {
    id: "documents.nudge",
    section: "documents",
    label: "A nudge fires",
    description: "A reminder you scheduled on a document, part, task or decision comes due.",
    appDefault: true,
    emailDefault: false,
    chatDefault: false,
  },
  // ── Workspace ────────────────────────────────────────────────────────────────────────────────
  {
    id: "collections.create",
    section: "workspace",
    label: "A new collection",
    description: "A collection is created in this workspace.",
    appDefault: false,
    emailDefault: false,
    chatDefault: false,
  },
  {
    id: "emails.invite_accepted",
    section: "workspace",
    label: "An invitation is accepted",
    description: "Someone you invited to the workspace joins.",
    appDefault: true,
    emailDefault: false,
    chatDefault: false,
  },
  {
    id: "documents.add_user",
    section: "workspace",
    label: "You're added to a document",
    description: "Someone gives you direct access to a document.",
    appDefault: true,
    emailDefault: false,
    chatDefault: false,
  },
  {
    id: "collections.add_user",
    section: "workspace",
    label: "You're added to a collection",
    description: "Someone gives you direct access to a collection.",
    appDefault: true,
    emailDefault: false,
    chatDefault: false,
  },
  {
    id: "emails.export_completed",
    section: "workspace",
    label: "An export finishes",
    description: "A workspace export you started is ready to download.",
    appDefault: true,
    emailDefault: false,
    chatDefault: false,
  },
  {
    id: "security.authz_denied",
    section: "workspace",
    label: "A permission check is denied",
    description:
      "Someone is refused access to something they tried to reach. Visible to admins only.",
    appDefault: true,
    emailDefault: false,
    chatDefault: true,
    adminOnly: true,
  },
  // ── Automation ───────────────────────────────────────────────────────────────────────────────
  {
    id: "runs.failed",
    section: "automation",
    label: "A run fails",
    description: "A flow run ends in failure.",
    appDefault: true,
    emailDefault: false,
    chatDefault: true,
  },
  {
    id: "runs.approval_requested",
    section: "automation",
    label: "A run needs your approval",
    description: "A run reaches a step that waits for a human decision.",
    appDefault: true,
    emailDefault: false,
    chatDefault: true,
  },
  {
    id: "runs.paused",
    section: "automation",
    label: "A run pauses",
    description: "A run is paused, by a person or by a budget.",
    appDefault: true,
    emailDefault: false,
    chatDefault: true,
  },
  {
    id: "runs.completed",
    section: "automation",
    label: "A run completes",
    description: "A flow run finishes successfully.",
    appDefault: false,
    emailDefault: false,
    chatDefault: false,
  },
  {
    id: "digests.completed",
    section: "automation",
    label: "A scheduled digest lands",
    description: "A recurring digest task finishes writing its document.",
    appDefault: true,
    emailDefault: false,
    chatDefault: false,
  },
  {
    id: "flows.published",
    section: "automation",
    label: "A flow is published",
    description: "A new version of a flow you can run is published.",
    appDefault: true,
    emailDefault: false,
    chatDefault: false,
  },
  {
    id: "tasks.fired",
    section: "automation",
    label: "A scheduled task fires",
    description: "A scheduled task in the Task Center runs.",
    appDefault: false,
    emailDefault: false,
    chatDefault: false,
  },
  {
    id: "ide.connected",
    section: "automation",
    label: "An editor window connects",
    description: "A VS Code window connects to this workspace for the first time.",
    appDefault: false,
    emailDefault: false,
    chatDefault: false,
  },
  // ── Market Pulse ─────────────────────────────────────────────────────────────────────────────
  {
    id: "pulse.decisions",
    section: "pulse",
    label: "A Pulse decision fires",
    description: "Market Pulse proposes a decision that meets its thresholds.",
    appDefault: true,
    emailDefault: false,
    chatDefault: false,
  },
  {
    id: "pulse.decisions.review",
    section: "pulse",
    label: "A Pulse decision needs review",
    description: "A proposed decision is held for human review before it acts.",
    appDefault: true,
    emailDefault: false,
    chatDefault: false,
  },
  {
    id: "pulse.data-health",
    section: "pulse",
    label: "A Pulse data-health warning",
    description: "A Market Pulse feed goes thin, floods, or otherwise drifts from its baseline.",
    appDefault: true,
    emailDefault: false,
    chatDefault: false,
  },
  {
    id: "pulse.paper",
    section: "pulse",
    label: "A Pulse paper-trade update",
    description: "A simulated position Market Pulse is tracking opens, closes or is stopped out.",
    appDefault: true,
    emailDefault: false,
    chatDefault: false,
  },
  // ── From KnowIt ──────────────────────────────────────────────────────────────────────────────
  {
    id: "emails.reminders",
    section: "fromKnowIt",
    label: "Occasional reminders",
    description:
      "A gentle nudge back to something you left open, when nothing else brought you back.",
    appDefault: false,
    emailDefault: false,
    chatDefault: false,
  },
  {
    id: "emails.onboarding",
    section: "fromKnowIt",
    label: "Onboarding tips",
    description: "A short series introducing what the workspace can do, for new cloud accounts.",
    appDefault: true,
    emailDefault: false,
    chatDefault: false,
    cloudOnly: true,
  },
  {
    id: "emails.features",
    section: "fromKnowIt",
    label: "New features",
    description: "Occasional word about something new, for cloud accounts only.",
    appDefault: true,
    emailDefault: false,
    chatDefault: false,
    cloudOnly: true,
  },
];

export const NOTIFICATION_SECTIONS: { id: NotificationSection; label: string }[] = [
  { id: "documents", label: "Documents and comments" },
  { id: "workspace", label: "Workspace" },
  { id: "automation", label: "Automation" },
  { id: "pulse", label: "Market Pulse" },
  { id: "fromKnowIt", label: "From KnowIt" },
];

const EVENT_BY_ID = new Map(NOTIFICATION_EVENTS.map((event) => [event.id, event]));

export function isKnownNotificationEvent(eventType: string): boolean {
  return EVENT_BY_ID.has(eventType);
}

export function notificationEventDef(eventType: string): NotificationEventDef | undefined {
  return EVENT_BY_ID.get(eventType);
}

type PreferenceValue = boolean | { app?: boolean; email?: boolean; chat?: boolean } | undefined;

/**
 * The scalar rule, exactly (`app/models/User.ts`'s owner delta, §D.2): a legacy `true` means
 * "in-app only" — NEVER read as email consent, no matter how tempting the shortcut looks. A
 * legacy `false` means all three channels off. A per-channel object reads its own key, falling
 * back to the event's server default when that key is absent. An id this table has never heard
 * of falls back to `app: true, email/chat: false` — the same posture an unknown legacy event
 * gets on the server.
 */
export function resolveChannel(
  settings: Record<string, PreferenceValue> | null | undefined,
  eventId: string,
  channel: NotificationChannel,
): boolean {
  const def = EVENT_BY_ID.get(eventId);
  const fallback =
    channel === "app"
      ? (def?.appDefault ?? true)
      : channel === "chat"
        ? (def?.chatDefault ?? false)
        : false;
  const value = settings?.[eventId];
  if (value === undefined) {
    return fallback;
  }
  if (typeof value === "boolean") {
    return channel === "app" ? value : false;
  }
  return value[channel] ?? fallback;
}

/** Where an inbox row's actor sentence + a plain link should send you (§A.5.2 N8, clean-room). */
export function notificationDestination(
  event: string,
  documentId: string | null,
): { to: string; params?: Record<string, string>; search?: Record<string, string> } | null {
  if (event.startsWith("runs.")) return { to: "/settings/runs" };
  if (event === "tasks.fired") return { to: "/settings/tasks" };
  if (event === "flows.published") return { to: "/settings/flows" };
  if (event.startsWith("ide.")) return { to: "/settings/connected-ides" };
  if (event === "security.authz_denied") return { to: "/settings/security" };
  if (event.startsWith("mail.")) return { to: "/mail" };
  if (event === "question.changed")
    return { to: "/knowledge/triggers", search: { tab: "questions" } };
  if (event === "watch.notified") return { to: "/knowledge/triggers", search: { tab: "watches" } };
  if (event === "code.attention") return { to: "/knowledge/triggers", search: { tab: "code" } };
  if (event.startsWith("pulse.")) return null;
  if (documentId) return { to: "/documents/$documentId", params: { documentId } };
  if (event.startsWith("collections.")) return { to: "/collections" };
  return null;
}

/** The inbox row's one-line sentence, as a `t()` key with interpolation params — never a
 * hard-coded English string, so Hebrew carries the same 28 sentences. */
export function notificationSentenceKey(event: string): string {
  return EVENT_SENTENCE[event] ?? "{{actor}} sent an update";
}

const EVENT_SENTENCE: Record<string, string> = {
  "documents.publish": "{{actor}} published a document",
  "documents.update": "{{actor}} updated a document you follow",
  "comments.create": "{{actor}} commented",
  "comments.mentioned": "{{actor}} mentioned you in a comment",
  "comments.resolve": "{{actor}} resolved a comment thread",
  document_chat_mention: "{{actor}} mentioned you in a document chat",
  "documents.nudge": "A nudge you scheduled is due",
  "collections.create": "{{actor}} created a collection",
  "emails.invite_accepted": "{{actor}} accepted your invitation",
  "documents.add_user": "{{actor}} gave you access to a document",
  "collections.add_user": "{{actor}} gave you access to a collection",
  "emails.export_completed": "Your export is ready",
  "security.authz_denied": "A permission check was denied",
  "runs.failed": "A run failed",
  "runs.approval_requested": "A run is waiting on your approval",
  "runs.paused": "A run was paused",
  "runs.completed": "A run completed",
  "digests.completed": "A scheduled digest finished",
  "flows.published": "A flow was published",
  "tasks.fired": "A scheduled task fired",
  "ide.connected": "An editor window connected",
  "pulse.decisions": "Market Pulse proposed a decision",
  "pulse.decisions.review": "A Pulse decision needs your review",
  "pulse.data-health": "A Pulse data-health warning",
  "pulse.paper": "A Pulse paper-trade update",
  "mail.alert": "A mail that matters arrived",
  "channel.alert": "Something you follow matters here",
  "question.changed": "A question you keep changed",
  "watch.notified": "A watch you set matched",
  "code.attention": "A pull request needs you",
};
